import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeftRight, Droplets, Flag, Footprints, Signpost, Tent, TriangleAlert } from 'lucide-react'
import {
  boundsOf,
  course,
  labelPoint,
  fitProjection,
  pathD,
  pointAtCourse,
  type CoursePoi,
  type LonLat,
} from '../lib/course'
import { useMeasure } from '../lib/useMeasure'

export type Units = 'km' | 'mi'
type View = 'course' | 'preserve'

type Mark = {
  key: string
  kind: 'km' | 'mile' | CoursePoi['kind']
  pos: LonLat
  /** First pass along the course, if the mark is on it */
  atM?: number
  short: string
  title: string
  detail: string
}

const fmtAt = (m: number, units: Units) =>
  units === 'km' ? `${(m / 1000).toFixed(m % 1000 ? 2 : 0)} km` : `${(m / 1609.344).toFixed(2)} mi`

function buildMarks(units: Units): Mark[] {
  const marks: Mark[] = []
  if (units === 'km') {
    // Out-and-back: km 1 & 4 share a spot, as do km 2 & 3.
    const groups = new Map<string, { pos: LonLat; labels: number[] }>()
    for (const m of course.markers.filter((x) => x.kind === 'km')) {
      const key = m.pos.join(',')
      const g = groups.get(key) ?? { pos: m.pos, labels: [] }
      g.labels.push(m.atM / 1000)
      groups.set(key, g)
    }
    for (const [key, g] of groups)
      marks.push({
        key: `km-${key}`,
        kind: 'km',
        pos: g.pos,
        atM: Math.min(...g.labels) * 1000,
        short: g.labels.join('·'),
        title: g.labels.map((l) => `${l} km`).join(' and '),
        detail: g.labels.length > 1 ? 'Passed on the way out and again on the way back' : '',
      })
  } else {
    for (const m of course.markers.filter((x) => x.kind === 'mile'))
      marks.push({ key: `mi-${m.atM}`, kind: 'mile', pos: m.pos, atM: m.atM, short: m.label.replace('Mile ', ''), title: m.label, detail: fmtAt(m.atM, 'km') })
  }
  for (const p of course.pois) {
    if (p.kind === 'start') continue
    marks.push({
      key: `${p.kind}-${p.atM[0]}`,
      kind: p.kind,
      pos: p.pos,
      atM: p.atM[0],
      short: '',
      title: p.label,
      detail: `${p.note}${p.atM.length ? ` · at ${p.atM.filter((m) => m > 0 && m < course.distanceM).map((m) => fmtAt(m, units)).join(' and ')}` : ''}`,
    })
  }
  return marks
}

const poiIcon: Record<string, ReactNode> = {
  water: <Droplets className="size-full" strokeWidth={2.4} />,
  crossing: <TriangleAlert className="size-full" strokeWidth={2.4} />,
  bridge: <ArrowLeftRight className="size-full" strokeWidth={2.4} />,
  junction: <Signpost className="size-full" strokeWidth={2.4} />,
  turnaround: <Footprints className="size-full" strokeWidth={2.4} />,
  grove: <Tent className="size-full" strokeWidth={2.4} />,
}

export function CourseMap({
  units,
  cursorM,
  compact = false,
  minimal = false,
  className = '',
}: {
  units: Units
  /** Distance along the course to highlight (driven by the elevation chart). */
  cursorM?: number | null
  compact?: boolean
  /** Just the route and the start/finish, with no markers or view switch (home page). */
  minimal?: boolean
  className?: string
}) {
  const [ref, width] = useMeasure<HTMLDivElement>(900)
  const [view, setView] = useState<View>('course')
  const [active, setActive] = useState<string | null>(null)

  const bounds = useMemo(() => {
    if (view === 'preserve') return boundsOf([...course.context.preserve, course.context.redLoop, ...course.context.lake, course.route], 0.03)
    // Course plus the start-area parking, with room for labels.
    const b = boundsOf([course.route, ...course.context.parking], 0.0)
    const [[a, c], [d, e]] = b
    // The same distance on every side, from the course's longer dimension
    const k = Math.cos((((c + e) / 2) * Math.PI) / 180)
    const padY = Math.max(e - c, (d - a) * k) * 0.14
    const padX = padY / k
    return [
      [a - padX, c - padY],
      [d + padX, e + padY * 2.2], // extra on top, clear of the view toggle
    ] as [LonLat, LonLat]
  }, [view])

  const maxH = compact ? 360 : Math.min(620, Math.max(320, width * 0.62))
  const proj = useMemo(() => fitProjection(bounds, width, maxH), [bounds, width, maxH])
  const { project } = proj
  const viewH = proj.height

  const layers = useMemo(() => {
    // Keep the lake label clear of the view toggle in the top-left corner.
    const [[bx0, by0], [bx1, by1]] = bounds
    const startPos = course.pois.find((p) => p.kind === 'start')!.pos
    const lakeLabel = labelPoint(
      course.context.lake,
      [
        [bx0 + (bx1 - bx0) * 0.25, by0],
        [bx1, by1 - (by1 - by0) * 0.18],
      ],
      36,
      [
        { at: startPos, meters: view === 'course' ? 260 : 700 },
        // and half a label's width (plus a nudged marker) off the route
        ...course.route.map((at) => ({ at, meters: (view === 'course' ? 80 : 65) * proj.metersPerUnit })),
      ],
    )
    const roads = course.context.roads.map((r) => {
      const pts = r.line.map(project)
      const visible = pts.filter(([x, y]) => x > 30 && x < width - 30 && y > 20 && y < viewH - 20)
      let label: { x: number; y: number; angle: number } | null = null
      if (visible.length >= 2) {
        const mid = Math.floor(visible.length / 2)
        const [x1, y1] = visible[Math.max(0, mid - 1)]
        const [x2, y2] = visible[Math.min(visible.length - 1, mid)]
        let angle = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI
        if (angle > 90) angle -= 180
        if (angle < -90) angle += 180
        label = { x: (x1 + x2) / 2, y: (y1 + y2) / 2, angle }
      }
      return { d: pathD(r.line, project), label: r.label, at: label }
    })
    const seenRoad = new Set<string>()
    return {
      preserve: course.context.preserve.map((r) => pathD(r, project, true)).join(' '),
      green: course.context.green.map((r) => pathD(r, project, true)).join(' '),
      lake: course.context.lake.map((r) => pathD(r, project, true)).join(' '),
      islands: course.context.lakeIslands.map((r) => pathD(r, project, true)).join(' '),
      streams: course.context.streams.map((s) => pathD(s, project)),
      redLoop: pathD(course.context.redLoop, project, true),
      spurs: course.context.spurs.map((s) => pathD(s, project)),
      parking: course.context.parking.map((p) => ({ d: pathD(p, project, true), c: project(centroid(p)) })),
      roads: roads.map((r) => {
        const showLabel = r.at && !seenRoad.has(r.label)
        if (showLabel) seenRoad.add(r.label)
        return { ...r, at: showLabel ? r.at : null }
      }),
      route: pathD(course.route, project),
      lakeLabel: lakeLabel ? project(lakeLabel) : null,
      arrows: [0.18, 0.42, 0.66, 0.88].map((f) => {
        const a = project(pointAtCourse(course.legM * f))
        const b = project(pointAtCourse(course.legM * f + 25))
        return { x: a[0], y: a[1], angle: (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI }
      }),
    }
  }, [project, proj.metersPerUnit, bounds, width, viewH, view])

  const marks = useMemo(() => (minimal ? [] : buildMarks(units)), [units, minimal])
  const start = project(course.pois.find((p) => p.kind === 'start')!.pos)
  const { placed, label: startLabel } = useMemo(() => layoutMarks(marks, project, view, width, viewH, layers.parking.slice(0, 4).map((p) => p.c)), [marks, project, view, width, viewH, layers.parking])
  const cursor = cursorM != null ? project(pointAtCourse(cursorM)) : null
  const activePlaced = placed.find((p) => p.m.key === active)
  const activeMark = activePlaced?.m
  const activeXY = activePlaced ? [activePlaced.x, activePlaced.y] : null
  const scaleM = view === 'course' ? 250 : 1000
  const scalePx = scaleM / proj.metersPerUnit

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-line bg-[var(--land)] ${className}`}>
      <div className={`absolute left-3 top-3 z-10 flex gap-1 rounded-lg border border-line bg-surface/95 p-1 text-sm shadow-sm ${minimal ? 'hidden' : ''}`} role="group" aria-label="Map view">
        {(['course', 'preserve'] as View[]).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => setView(v)}
            className={`rounded-md px-2.5 py-1 font-semibold ${view === v ? 'bg-brand text-on-brand' : 'text-ink-2 hover:bg-surface-2'}`}
          >
            {v === 'course' ? 'Course' : 'Whole preserve'}
          </button>
        ))}
      </div>

      <div ref={ref} className="w-full">
        <svg
          viewBox={`0 0 ${width} ${viewH}`}
          width={width}
          height={viewH}
          className="block h-auto w-full touch-pan-y select-none"
          role="img"
          aria-label={`Map of the ${course.distanceM / 1000} km out-and-back course in Busse Woods, starting beside Grove 5 at Busse Forest South, crossing Higgins Road on the trail bridge and heading north past the Elk Pasture to a turnaround, and back.`}
        >
          <rect x={0} y={0} width={width} height={viewH} fill="var(--land)" />
          <path d={layers.preserve} fill="var(--forest)" fillRule="evenodd" />
          <path d={layers.green} fill="var(--forest)" />
          {layers.parking.map((p, i) => (
            <path key={i} d={p.d} fill="var(--surface-2)" stroke="var(--road)" strokeWidth={1} />
          ))}
          {layers.roads.map((r, i) => (
            <path key={i} d={r.d} fill="none" stroke="var(--road)" strokeWidth={view === 'course' ? 7 : 4} strokeLinecap="round" strokeLinejoin="round" />
          ))}
          {layers.streams.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="var(--lake)" strokeOpacity={0.55} strokeWidth={1.5} />
          ))}
          <path d={layers.lake} fill="var(--lake-fill)" stroke="var(--lake)" strokeWidth={1.2} fillRule="evenodd" />
          <path d={layers.islands} fill="var(--forest)" stroke="var(--lake)" strokeWidth={0.8} />
          <path d={layers.redLoop} fill="none" stroke="var(--map-label)" strokeOpacity={0.55} strokeWidth={1.6} strokeDasharray="5 4" />
          {layers.spurs.map((d, i) => (
            <path key={i} d={d} fill="none" stroke="var(--map-label)" strokeOpacity={0.4} strokeWidth={1.2} strokeDasharray="3 4" />
          ))}

          {layers.lakeLabel && (
            <text
              x={layers.lakeLabel[0]}
              y={layers.lakeLabel[1]}
              textAnchor="middle"
              fill="var(--lake)"
              className="font-display"
              style={{ fontSize: view === 'course' ? 22 : 18, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase' }}
            >
              Busse Lake
            </text>
          )}
          {layers.roads.map(
            (r, i) =>
              r.at && (
                <text
                  key={`rl-${i}`}
                  transform={`translate(${r.at.x} ${r.at.y}) rotate(${r.at.angle})`}
                  dy={-7}
                  textAnchor="middle"
                  fill="var(--map-label)"
                  style={{ fontSize: 11.5, fontWeight: 600 }}
                  stroke="var(--land)"
                  strokeWidth={3}
                  paintOrder="stroke"
                >
                  {r.label}
                </text>
              ),
          )}
          {view === 'course' &&
            !minimal &&
            layers.parking.slice(0, 4).map((p, i) => (
              <g key={`p-${i}`} transform={`translate(${p.c[0]} ${p.c[1]})`} aria-hidden>
                <rect x={-8} y={-8} width={16} height={16} rx={3} fill="var(--lake)" />
                <text y={4.5} textAnchor="middle" fill="var(--surface)" style={{ fontSize: 12, fontWeight: 800 }}>
                  P
                </text>
              </g>
            ))}

          {/* The route: a surface-colored casing keeps it legible over everything */}
          <path d={layers.route} fill="none" stroke="var(--surface)" strokeWidth={view === 'course' ? 11 : 7} strokeLinecap="round" strokeLinejoin="round" />
          <path
            d={layers.route}
            fill="none"
            stroke="var(--route)"
            strokeWidth={view === 'course' ? 6 : 4}
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1000}
            strokeDasharray={1000}
            style={{ animation: 'draw-route 1.6s 120ms cubic-bezier(0.4,0,0.2,1) both', ['--route-length' as string]: 1000 }}
          />
          {view === 'course' &&
            !minimal &&
            layers.arrows.map((a, i) => (
              <path key={i} d="M-4,-4 L2,0 L-4,4" transform={`translate(${a.x} ${a.y}) rotate(${a.angle})`} fill="none" stroke="var(--surface)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
            ))}

          {placed.map(({ m, x, y, ax, ay, r }) => {
            const isActive = active === m.key
            const isDistance = m.kind === 'km' || m.kind === 'mile'
            return (
              <g
                key={m.key}
                transform={`translate(${x} ${y})`}
                tabIndex={0}
                role="button"
                aria-label={`${m.title}. ${m.detail}`}
                onMouseEnter={() => setActive(m.key)}
                onMouseLeave={() => setActive((a) => (a === m.key ? null : a))}
                onFocus={() => setActive(m.key)}
                onBlur={() => setActive((a) => (a === m.key ? null : a))}
                onClick={() => setActive((a) => (a === m.key ? null : m.key))}
                className="cursor-pointer outline-none"
              >
                <circle r={r + 8} fill="transparent" />
                {(ax !== x || ay !== y) && (
                  <>
                    <line x1={ax - x} y1={ay - y} x2={0} y2={0} stroke={poiColor(m.kind)} strokeWidth={2} />
                    <circle cx={ax - x} cy={ay - y} r={3} fill={poiColor(m.kind)} stroke="var(--surface)" strokeWidth={1.5} />
                  </>
                )}
                {isDistance ? (
                  <>
                    <circle r={r} fill={m.kind === 'km' ? 'var(--brand)' : 'var(--surface)'} stroke={m.kind === 'km' ? 'var(--surface)' : 'var(--brand)'} strokeWidth={2.5} />
                    <text y={4} textAnchor="middle" fill={m.kind === 'km' ? 'var(--on-brand)' : 'var(--brand)'} style={{ fontSize: m.short.length > 2 ? 9.5 : 11.5, fontWeight: 800 }}>
                      {m.short}
                    </text>
                  </>
                ) : (
                  <>
                    <circle r={r} fill={m.kind === 'grove' ? 'var(--brand)' : 'var(--surface)'} stroke={poiColor(m.kind)} strokeWidth={2.5} />
                    <foreignObject x={-r * 0.55} y={-r * 0.55} width={r * 1.1} height={r * 1.1} style={{ color: m.kind === 'grove' ? 'var(--on-brand)' : poiColor(m.kind) }}>
                      {poiIcon[m.kind]}
                    </foreignObject>
                  </>
                )}
                {isActive && <circle r={r + 4} fill="none" stroke="var(--ink)" strokeWidth={1.5} />}
              </g>
            )
          })}

          {/* Start / finish */}
          <g transform={`translate(${start[0]} ${start[1]})`} aria-hidden>
            <circle r={view === 'course' ? 15 : 10} fill="var(--ink)" stroke="var(--surface)" strokeWidth={3} />
            <foreignObject x={-7} y={-7} width={14} height={14} style={{ color: 'var(--surface)' }}>
              <Flag className="size-full" strokeWidth={2.6} />
            </foreignObject>
            <text x={startLabel.x} y={startLabel.y} textAnchor={startLabel.anchor} fill="var(--ink)" stroke="var(--land)" strokeWidth={4} paintOrder="stroke" style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Start · Finish
            </text>
          </g>

          {cursor && (
            <g transform={`translate(${cursor[0]} ${cursor[1]})`} aria-hidden>
              <circle r={11} fill="var(--route)" fillOpacity={0.25} />
              <circle r={6} fill="var(--ink)" stroke="var(--surface)" strokeWidth={2.5} />
            </g>
          )}

          {/* Scale bar */}
          <g transform={`translate(${width - scalePx - 18} ${viewH - 18})`} aria-hidden>
            <rect x={-6} y={-16} width={scalePx + 12} height={24} rx={4} fill="var(--surface)" fillOpacity={0.85} />
            <path d={`M0,0 H${scalePx}`} stroke="var(--ink)" strokeWidth={2} />
            <path d={`M0,-4 V0 M${scalePx},-4 V0`} stroke="var(--ink)" strokeWidth={2} />
            <text x={scalePx / 2} y={-5} textAnchor="middle" fill="var(--ink)" style={{ fontSize: 10.5, fontWeight: 700 }}>
              {scaleM >= 1000 ? `${scaleM / 1000} km` : `${scaleM} m`}
            </text>
          </g>
          {/* North arrow */}
          <g transform={`translate(${width - 26} 30)`} aria-hidden>
            <path d="M0,-12 L6,6 L0,2 L-6,6 Z" fill="var(--ink)" />
            <text y={19} textAnchor="middle" fill="var(--ink)" style={{ fontSize: 10, fontWeight: 800 }}>
              N
            </text>
          </g>
        </svg>
      </div>

      {activeMark && activeXY && (
        <div
          className="pointer-events-none absolute z-20 w-max max-w-[15rem] -translate-x-1/2 rounded-lg border border-line bg-surface px-3 py-2 text-sm shadow-card"
          style={{
            left: `${Math.min(Math.max((activeXY[0] / width) * 100, 18), 82)}%`,
            top: `${(activeXY[1] / viewH) * 100}%`,
            transform: `translate(-50%, ${activeXY[1] < viewH / 2 ? '22px' : 'calc(-100% - 22px)'})`,
          }}
          role="status"
        >
          <p className="font-semibold text-ink">{activeMark.title}</p>
          {activeMark.detail && <p className="text-ink-2">{activeMark.detail}</p>}
        </div>
      )}

      <MapCredit />
    </div>
  )
}

/**
 * OpenStreetMap's licence asks for a credit beside the map. It shows on load, then folds into a
 * small (i) button after a few seconds, which its attribution guidelines allow.
 */
export function MapCredit() {
  const [open, setOpen] = useState(true)
  useEffect(() => {
    const t = setTimeout(() => setOpen(false), 5000)
    return () => clearTimeout(t)
  }, [])
  return (
    <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1 text-[0.7rem]" style={{ color: 'var(--map-label)' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Map credits"
        className="grid size-5 place-items-center rounded-full border border-current font-serif text-[0.7rem] italic leading-none opacity-70 hover:opacity-100"
      >
        i
      </button>
      {open && (
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="rounded bg-surface/80 px-1.5 py-0.5 hover:underline">
          © OpenStreetMap
        </a>
      )}
    </div>
  )
}

type Placed = { m: Mark; x: number; y: number; ax: number; ay: number; r: number }
type Project = (p: LonLat) => [number, number]
type Box = { left: number; top: number; w: number; h: number }

const hitsBox = (b: Box, x: number, y: number, r: number) =>
  x + r > b.left && x - r < b.left + b.w && y + r > b.top && y - r < b.top + b.h

/**
 * Lays out the map's marks without overlaps:
 * 1. Off-course marks (the groves) stay at their true spots.
 * 2. The start/finish label goes where it covers the fewest of them and none of the route near the start.
 * 3. Course marks that would sit on top of something (the water station is 30 m before the 1 km marker)
 *    are nudged sideways off the route, with a short leader line back to their true spot.
 */
function layoutMarks(marks: Mark[], project: Project, view: View, width: number, height: number, parkingIcons: [number, number][]) {
  const radius = (m: Mark) => (view === 'course' ? (m.kind === 'km' || m.kind === 'mile' ? 13 : 12) : 9)
  const [sx, sy] = project(course.pois.find((p) => p.kind === 'start')!.pos)
  const startR = view === 'course' ? 15 : 10
  const placed: Placed[] = []
  for (const m of marks.filter((x) => x.atM == null)) {
    const [ax, ay] = project(m.pos)
    const r = radius(m)
    const free = (x: number, y: number) =>
      Math.hypot(x - sx, y - sy) >= startR + r + 2 && placed.every((o) => Math.hypot(o.x - x, o.y - y) >= o.r + r + 2)
    let [x, y] = [ax, ay]
    if (!free(x, y)) {
      // a grove right beside the start line steps out from under the flag (and its neighbor),
      // fanning out around the flag from the side it's really on
      const d = Math.hypot(ax - sx, ay - sy)
      const base = d > 0.5 ? Math.atan2(ay - sy, ax - sx) : 0
      const ring = Math.max(d, startR + r + 6)
      const spots = [0, 0.6, -0.6, 1.2, -1.2, 1.8, -1.8, 2.4, -2.4].map((k) => [sx + Math.cos(base + k) * ring, sy + Math.sin(base + k) * ring])
      ;[x, y] = spots.find(([px, py]) => free(px, py)) ?? spots[0]
    }
    placed.push({ m, x, y, ax, ay, r })
  }

  const w = 94
  const h = 14
  const nearStart = Array.from({ length: 21 }, (_, i) => project(pointAtCourse(i * 20)))
  const candidates = [
    { x: 20, y: -10, anchor: 'start' },
    { x: 20, y: 22, anchor: 'start' },
    { x: 0, y: -26, anchor: 'middle' },
    { x: 0, y: 36, anchor: 'middle' },
    { x: -20, y: -10, anchor: 'end' },
    { x: -20, y: 22, anchor: 'end' },
  ] as const
  const boxOf = (c: (typeof candidates)[number]): Box => ({
    left: sx + c.x - (c.anchor === 'middle' ? w / 2 : c.anchor === 'end' ? w : 0),
    top: sy + c.y - h + 3,
    w,
    h,
  })
  const score = (c: (typeof candidates)[number]) => {
    const b = boxOf(c)
    let n = placed.filter((p) => hitsBox(b, p.x, p.y, p.r + 2)).length * 3
    n += nearStart.filter(([px, py]) => hitsBox(b, px, py, 4)).length
    if (view === 'course') n += parkingIcons.filter(([px, py]) => hitsBox(b, px, py, 9)).length * 2
    if (b.left < 4 || b.left + w > width - 4 || b.top < 4 || b.top + h > height - 4) n += 100
    return n
  }
  const label = candidates.reduce((best, c) => (score(c) < score(best) ? c : best))
  const labelBox = boxOf(label)

  const clashes = (x: number, y: number, r: number) =>
    Math.hypot(sx - x, sy - y) < startR + r + 2 ||
    hitsBox(labelBox, x, y, r + 1) ||
    placed.some((o) => Math.hypot(o.x - x, o.y - y) < o.r + r + 2)
  for (const m of marks.filter((x) => x.atM != null)) {
    const [ax, ay] = project(m.pos)
    const r = radius(m)
    let [x, y] = [ax, ay]
    if (clashes(x, y, r)) {
      const [x1, y1] = project(pointAtCourse(Math.max(0, m.atM! - 15)))
      const [x2, y2] = project(pointAtCourse(m.atM! + 15))
      const len = Math.hypot(x2 - x1, y2 - y1) || 1
      const [nx, ny] = [(y2 - y1) / len, -(x2 - x1) / len] // left of the direction of travel
      const step = 2 * r + 8
      const options = [1, -1, 1.6, -1.6, 2.2, -2.2].map((k) => [ax + nx * step * k, ay + ny * step * k])
      const crossesLabel = (ox: number, oy: number) =>
        Array.from({ length: 9 }, (_, i) => i / 8).some((t) => hitsBox(labelBox, ax + (ox - ax) * t, ay + (oy - ay) * t, 1))
      ;[x, y] =
        options.find(([ox, oy]) => !clashes(ox, oy, r) && !crossesLabel(ox, oy)) ??
        options.find(([ox, oy]) => !clashes(ox, oy, r)) ??
        options[0]
    }
    placed.push({ m, x, y, ax, ay, r })
  }
  return { placed, label }
}

const poiColor = (kind: string) =>
  kind === 'water' ? 'var(--lake)' : kind === 'turnaround' ? 'var(--accent)' : kind === 'grove' ? 'var(--surface)' : 'var(--warn)'

function centroid(poly: LonLat[]): LonLat {
  const n = poly.length
  return [poly.reduce((s, p) => s + p[0], 0) / n, poly.reduce((s, p) => s + p[1], 0) / n]
}

export function MapLegend({ units }: { units: Units }) {
  const has = (kind: CoursePoi['kind']) => course.pois.some((p) => p.kind === kind)
  const groves = course.pois.filter((p) => p.kind === 'grove').map((p) => p.label)
  const item = (swatch: ReactNode, label: string) => (
    <li className="flex items-center gap-2">
      <span className="grid w-7 place-items-center" aria-hidden>
        {swatch}
      </span>
      {label}
    </li>
  )
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-2">
      {item(<span className="h-1.5 w-7 rounded-full bg-[var(--route)]" />, '5K route (out and back)')}
      {item(<span className="size-4 rounded-full border-2 border-surface bg-ink" />, 'Start & finish')}
      {item(
        units === 'km' ? <span className="size-4 rounded-full bg-brand" /> : <span className="size-4 rounded-full border-2 border-brand bg-surface" />,
        units === 'km' ? 'Kilometer markers' : 'Mile markers',
      )}
      {item(<Droplets className="size-4 text-lake" />, 'Water')}
      {has('crossing') && item(<TriangleAlert className="size-4 text-warn" />, 'Marshalled crossing')}
      {has('junction') && item(<Signpost className="size-4 text-warn" />, 'Junction or tight corner (marshal)')}
      {item(<ArrowLeftRight className="size-4 text-warn" />, 'Bridge')}
      {item(<span className="grid size-5 place-items-center rounded-full bg-brand text-on-brand"><Tent className="size-3" /></span>, `Race HQ: ${groves.join(' & ')}`)}
      {item(<span className="h-0 w-7 border-t-2 border-dashed border-ink-3" />, 'Other paved trails')}
    </ul>
  )
}
